import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import {
  useCreateScenario as useCreateScenarioMutation,
  useCreateYardProperties,
  useGetDefaultYardProperties,
  useGetYard,
} from '@/api/generated';
import { parseCutoffMs } from '@/components/blocks/berth-plan/berth-plan.utils';
import { useOpen } from '@/hooks/use-open';
import { dateTimeLocalToISOString } from '@/lib/utils';
import { suggestStartTimeFromYard } from '@/routes/(app)/(scenarios)/(create)/create-scenario.utils';
import {
  CreateScenarioSchema,
  createScenarioSchema,
} from '@/routes/(app)/(scenarios)/(create)/types';
import { isNotFoundError } from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

// Matches TanStack Query's default retry count.
const DEFAULT_QUERY_RETRIES = 3;

export const useCreateScenarioForm = () => {
  const { t } = useTranslation();
  const { mutateAsync: createScenario, isPending: isCreatingScenario } =
    useCreateScenarioMutation();
  const {
    data: defaultYardProperties,
    isLoading: isLoadingYardProperties,
    error: yardPropertiesError,
  } = useGetDefaultYardProperties({
    query: {
      // A 404 means no default has been configured yet, not a failure worth retrying.
      retry: (failureCount, error) =>
        !isNotFoundError(error) && failureCount < DEFAULT_QUERY_RETRIES,
    },
  });
  const hasYardPropertiesError =
    !!yardPropertiesError && !isNotFoundError(yardPropertiesError);
  const {
    mutateAsync: createYardProperties,
    isPending: isCreatingYardProperties,
  } = useCreateYardProperties();
  const isSubmittingRef = useRef(false);
  const configureDialog = useOpen({ defaultOpen: false });

  const form = useForm<CreateScenarioSchema>({
    resolver: zodResolver(createScenarioSchema),
    defaultValues: {
      yardId: undefined,
      planningId: undefined,
      simulationRuns: 50,
      startTime: '',
      durationDays: 14,
      teuCapacity: undefined,
      reeferCapacity: undefined,
      explicitTransshipmentTransfers: [],
    },
  });

  useEffect(() => {
    if (!defaultYardProperties) return;
    if (form.getValues('teuCapacity') !== undefined) return;
    form.setValue('teuCapacity', defaultYardProperties.teu_capacity);
    form.setValue('reeferCapacity', defaultYardProperties.n_reefer_capacity);
  }, [defaultYardProperties, form]);

  const {
    yardId,
    planningId,
    simulationRuns,
    startTime,
    durationDays,
    teuCapacity,
    reeferCapacity,
    explicitTransshipmentTransfers,
  } = form.watch();

  useEffect(() => {
    form.setValue('explicitTransshipmentTransfers', []);
  }, [planningId, form]);

  const { data: selectedYard } = useGetYard(yardId, {
    query: { enabled: !!yardId },
  });
  const selectedYardLastTimeIn = selectedYard?.last_time_in;
  // Only the yard's last gate-in time: the current time never locks visits.
  const planningCutoffMs = parseCutoffMs(selectedYardLastTimeIn);

  // Re-applied whenever the selected yard's suggestion changes; later edits for the same yard are kept.
  useEffect(() => {
    const suggestedStartTime = suggestStartTimeFromYard(selectedYardLastTimeIn);
    form.setValue('startTime', suggestedStartTime ?? '');
  }, [selectedYardLastTimeIn, form]);

  const isPlanningStepEnabled = !!yardId;
  const areSourcesSelected = !!yardId && !!planningId;

  const resolveYardPropertiesId = useCallback(async (): Promise<
    string | undefined
  > => {
    const hasTeu = teuCapacity !== undefined;
    const hasReefer = reeferCapacity !== undefined;
    if (hasTeu !== hasReefer) {
      throw new Error('Both capacities are required when either is set');
    }
    if (!hasTeu || !hasReefer) {
      return defaultYardProperties?.id;
    }
    if (
      teuCapacity === defaultYardProperties?.teu_capacity &&
      reeferCapacity === defaultYardProperties.n_reefer_capacity
    ) {
      return defaultYardProperties.id;
    }
    const created = await createYardProperties({
      data: {
        name: `Custom capacity ${new Date().toLocaleString()}`,
        teu_capacity: teuCapacity,
        n_reefer_capacity: reeferCapacity,
      },
    });
    return created.id;
  }, [
    createYardProperties,
    defaultYardProperties,
    teuCapacity,
    reeferCapacity,
  ]);

  const handleSubmit = useCallback(async (): Promise<string | undefined> => {
    if (isSubmittingRef.current || isCreatingScenario) return undefined;
    isSubmittingRef.current = true;

    let yardPropertiesId: string | undefined;
    try {
      yardPropertiesId = await resolveYardPropertiesId();
    } catch {
      isSubmittingRef.current = false;
      toast.error(t('general.toast.somethingWentWrong'));
      return undefined;
    }

    try {
      const response = await createScenario(
        {
          data: {
            yard_id: yardId,
            planning_id: planningId,
            total_simulation_runs: simulationRuns,
            start_time: dateTimeLocalToISOString(startTime),
            duration_days: durationDays,
            yard_properties_id: yardPropertiesId,
            explicit_transshipment_transfers:
              explicitTransshipmentTransfers.map((transfer) => ({
                source_ship_visit_id: transfer.sourceShipVisitId,
                target_ship_visit_id: transfer.targetShipVisitId,
                n_containers: transfer.nContainers,
              })),
          },
        },
        {
          onError: () => {
            toast.error(t('general.toast.somethingWentWrong'));
          },
        }
      );

      return response.id;
    } finally {
      isSubmittingRef.current = false;
    }
  }, [
    createScenario,
    resolveYardPropertiesId,
    yardId,
    planningId,
    simulationRuns,
    startTime,
    durationDays,
    explicitTransshipmentTransfers,
    t,
    isCreatingScenario,
  ]);

  return {
    form,
    configureDialog,
    isPlanningStepEnabled,
    areSourcesSelected,
    isLoadingYardProperties,
    hasYardPropertiesError,
    isCreatingScenario: isCreatingScenario || isCreatingYardProperties,
    handleSubmit,
    planningCutoffMs,
  };
};
